import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';

const NotificationContext = createContext(null);
const WS_BASE = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);

  const connectWs = useCallback(() => {
    if (!user || !WS_BASE) return;

    // Get token from cookie (not directly accessible) — use API to get it
    const token = document.cookie.split('; ').find(c => c.startsWith('access_token='))?.split('=')[1];
    if (!token) return;

    const ws = new WebSocket(`${WS_BASE}/api/ws/notifications?token=${token}`);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'unread_alerts') {
          setNotifications(prev => {
            const ids = new Set(prev.map(n => n.alert_id));
            const newAlerts = data.alerts.filter(a => !ids.has(a.alert_id));
            return [...newAlerts, ...prev];
          });
          setUnreadCount(data.count);
        } else if (data.type === 'price_drop') {
          setNotifications(prev => [data, ...prev]);
          setUnreadCount(prev => prev + 1);
          // Show browser notification if permitted
          if (Notification.permission === 'granted') {
            new Notification(`Price Drop: ${data.shoe_name}`, {
              body: `Now ${data.currency === 'GBP' ? '£' : data.currency === 'AUD' ? 'A$' : '$'}${data.new_price} (${data.drop_pct}% off) at ${data.store_name}`,
              icon: '/favicon.ico',
            });
          }
        } else if (data.type === 'new_product') {
          setNotifications(prev => [data, ...prev]);
          setUnreadCount(prev => prev + 1);
        } else if (data.type === 'alert_read') {
          setNotifications(prev => prev.map(n => n.alert_id === data.alert_id ? { ...n, read: true } : n));
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      } catch (e) {
        console.error('WS message parse error:', e);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      // Reconnect after 5 seconds
      reconnectTimer.current = setTimeout(connectWs, 5000);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [user]);

  useEffect(() => {
    if (user) {
      // Request notification permission
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
      connectWs();
    }
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [user, connectWs]);

  const markRead = useCallback((alertId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'mark_read', alert_id: alertId }));
    }
  }, []);

  const markAllRead = useCallback(() => {
    notifications.filter(n => !n.read).forEach(n => {
      if (n.alert_id) markRead(n.alert_id);
    });
  }, [notifications, markRead]);

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, connected, markRead, markAllRead }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationContext);
}
